import ItemStatus from '@/Enums/ItemStatus.enum';
import { ILineItemCreative } from '@/Interfaces/CampaignManagement/LineItem.interface';
import { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import { GridPaginationAndSortFilters } from '@/components/Grid/hooks/useGridApiPaginationAndSort';
import CreateCreativeIcon from '@/components/Icons/CreateCreativeIcon';
import ResetAudienceIcon from '@/components/Icons/ResetAudienceIcon';
import { getAssociatedLineItemCreatives, mapLineItemCreativesToFormStructure } from '@/helpers/campaignManagement';
import { getCreativeDefaultTime } from '@/helpers/campaignManagement/creatives.helpers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectLineItemCreatives } from '@/store/selectors/campaignManagement.selectors';
import { setFilters } from '@/store/slices/creativeSlice';
import { Add } from '@mui/icons-material';
import { Button } from '@mui/material';
import type { GridRowId } from '@mui/x-data-grid-pro';
import classNames from 'classnames';
import moment, { type Moment } from 'moment';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import AssociateCreativesModal from './Modal';
import CreativesTable from './Table';
import { getColumns } from './Table/columns';
import PixelsModal from './Table/components/PixelsModal';

const CreativesSection = ({ index }: { index: number }) => {
  const dispatch = useAppDispatch();

  const loading: boolean = !!useAppSelector(({ campaignManagement }) => campaignManagement.activeCampaignRequestId);
  const originalAssociatedCreatives = useAppSelector(state => selectLineItemCreatives(state, index));
  const {
    setValue,
    formState: { errors },
  } = useFormContext();

  const associatedCreatives: ILineItemCreative[] = useWatch({ name: 'creatives' });

  const rows = associatedCreatives.map((creative, index) => ({
    ...creative,
    creativeIndex: index,
  }));

  const [isDirty, setIsDirty] = useState(false);
  const setAssociatedCreatives = useCallback(
    (creatives: ILineItemCreative[]) => {
      setValue('creatives', creatives, { shouldDirty: true, shouldValidate: true });
      setIsDirty(true);
    },
    [setValue],
  );

  const [watchLineItemStartDate, watchLineItemEndDate, watchLineItemChannelOption] = useWatch({
    name: ['startDate', 'endDate', 'channel'],
  });
  const watchLineItemChannel = watchLineItemChannelOption?.label;

  const [rowCreativeTableSelectionModel, setRowCreativeTableSelectionModel] = useState<GridRowId[]>([]);
  const [isAssociateCreativesModalOpen, setIsAssociateCreativesModalOpen] = useState(false);

  const [pixelsModalOpen, setPixelsModalOpen] = useState(false);
  const [activeCreativeIndex, setActiveCreativeIndex] = useState<number | null>(null);

  const handleRelativeWeightChange = useCallback(
    (id: number) =>
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        const numberValue = Number.parseInt(value) || 0;
        const resetCurrent = numberValue === 0;
        const totalRelativeWeight = associatedCreatives.reduce(
          (acc: number, { id: creativeId, associationWeightRel }: ILineItemCreative) =>
            creativeId === id ? acc : acc + (associationWeightRel || 0),
          numberValue,
        );
        setAssociatedCreatives(
          associatedCreatives.map(creative => {
            const associationWeightRel: number | '' =
              creative.id === id ? (resetCurrent ? '' : numberValue) : creative.associationWeightRel;
            const associationWeightPct: number | '' =
              totalRelativeWeight > 0 ? (associationWeightRel || 0) / totalRelativeWeight : 0;

            return {
              ...creative,
              associationWeightRel,
              associationWeightPct,
            };
          }),
        );
      },
    [associatedCreatives, setAssociatedCreatives],
  );

  const handleFieldChange = useCallback(
    (
      id: number,
      field: keyof Pick<
        ILineItemCreative,
        'associationStatus' | 'startDate' | 'endDate' | 'clickUrl' | 'disassociated'
      >,
    ) =>
      (value: ItemStatus | string | null | boolean | Moment) => {
        let newValue: ItemStatus | string | null | boolean | Moment = value;

        if (moment.isMoment(value) && value.isValid()) {
          const valUtc = moment.utc(value);
          const currentCreative = associatedCreatives.find(c => c.id === id);
          const currentVal = currentCreative?.[field as 'startDate' | 'endDate'];
          const currentValUtc = currentVal ? moment.utc(currentVal) : null;

          if (!currentValUtc || !currentValUtc.isSame(valUtc, 'day')) {
            const watchLineItemDate = field === 'startDate' ? watchLineItemStartDate : watchLineItemEndDate;
            const liDateUtc = watchLineItemDate ? moment.utc(watchLineItemDate) : null;
            newValue = getCreativeDefaultTime(valUtc, liDateUtc, field);
          }
        } else if (value && typeof value === 'string') {
          const m = moment(value);
          if (m.isValid()) {
            newValue = getCreativeDefaultTime(moment.utc(m), null, field);
          }
        }

        setAssociatedCreatives(
          associatedCreatives.map(creative =>
            creative.id === id
              ? {
                  ...creative,
                  [field]: newValue,
                }
              : creative,
          ),
        );
      },
    [associatedCreatives, setAssociatedCreatives],
  );

  const handleResetChanges = () => {
    setAssociatedCreatives(mapLineItemCreativesToFormStructure(originalAssociatedCreatives) || []);
    setRowCreativeTableSelectionModel([]);
    setIsDirty(false);
  };

  const handleAssociateCreatives = useCallback(
    (newAssociatedCreatives: ICreative[]) => {
      const associatedCreativesTotalWeight =
        (associatedCreatives || []).reduce(
          (acc, { associationWeightRel }) => acc + (associationWeightRel as number),
          0,
        ) + newAssociatedCreatives.length;

      setAssociatedCreatives(
        getAssociatedLineItemCreatives(
          [...associatedCreatives, ...newAssociatedCreatives],
          associatedCreativesTotalWeight,
        ),
      );
    },
    [associatedCreatives, setAssociatedCreatives, watchLineItemStartDate, watchLineItemEndDate],
  );

  const handleViewPixels = useCallback((index: number) => {
    setPixelsModalOpen(true);
    setActiveCreativeIndex(index);
  }, []);

  const handlePixelsClose = useCallback(() => {
    setPixelsModalOpen(false);
    setActiveCreativeIndex(null);
  }, []);

  const handlePixelsChange = useCallback(
    (pixels: string[]) => {
      setValue(`creatives.${activeCreativeIndex}.pixels`, pixels, { shouldDirty: true });
    },
    [setValue, activeCreativeIndex],
  );

  const creativeIds = useMemo(() => associatedCreatives.map(creative => creative.id), [associatedCreatives]);

  const columns = getColumns({
    handleRelativeWeightChange,
    handleFieldChange,
    handleViewPixels,
    creativeMinAssociateDate: watchLineItemStartDate,
    creativeMaxAssociateDate: watchLineItemEndDate,
    creativeIds,
    formErrors: errors,
  });

  const hasAssociatedCreatives = !loading && associatedCreatives?.length > 0;

  const setTableFilters = useCallback(
    (filters: GridPaginationAndSortFilters) => dispatch(setFilters(filters)),
    [dispatch],
  );

  return (
    <div className='h-full'>
      <div
        className={classNames(
          'flex items-center',
          hasAssociatedCreatives ? 'justify-between' : 'flex-col justify-center gap-6 h-full',
        )}
      >
        {hasAssociatedCreatives ? (
          <span className='text-center text-lg text-[#1E1E1E] font-semibold'>Associated Creatives</span>
        ) : (
          <div className='flex flex-col justify-center items-center gap-6'>
            <CreateCreativeIcon width={202} height={205} />
            <span className='text-xl font-semibold'>No Creatives Associated Yet</span>
          </div>
        )}
        <div className='flex gap-2'>
          {isDirty && (
            <Button variant='outlined' startIcon={<ResetAudienceIcon />} onClick={handleResetChanges}>
              Reset Changes
            </Button>
          )}
          <Button
            variant='contained'
            disabled={!watchLineItemChannel}
            onClick={() => setIsAssociateCreativesModalOpen(true)}
          >
            <Add sx={{ width: 20, height: 20, marginRight: '4px' }} />
            Associate New {!hasAssociatedCreatives && 'Creative'}
          </Button>
        </div>
      </div>
      <AssociateCreativesModal
        open={isAssociateCreativesModalOpen}
        handleClose={() => setIsAssociateCreativesModalOpen(false)}
        associatedCreatives={associatedCreatives}
        handleAssociateCreatives={handleAssociateCreatives}
        channel={watchLineItemChannel}
      />
      {activeCreativeIndex !== null && (
        <PixelsModal
          open={pixelsModalOpen}
          onClose={handlePixelsClose}
          pixels={associatedCreatives[activeCreativeIndex].pixels}
          onSubmit={handlePixelsChange}
        />
      )}
      {hasAssociatedCreatives && (
        <div className='my-4'>
          <CreativesTable
            tableData={rows}
            columns={columns}
            checkboxSelection={false}
            rowSelectionModel={rowCreativeTableSelectionModel}
            // @ts-ignore
            handleRowSelectionModelChange={setRowCreativeTableSelectionModel}
            withoutPagination
            setTableFilters={setTableFilters}
          />
        </div>
      )}
    </div>
  );
};

export default CreativesSection;
